import React, { useState } from 'react';
import { LockIcon } from 'lucide-react';
import { AuditEntry, ConfirmationModal, Drawer, Panel } from './AdminPrimitives';
import { Button } from '../ui/Button';
import { recordAudit, roleLabels, type AuditRecord } from '../../data/admin';
import type { ConfigParam } from '../../data/engagementAdmin';
import { useLocale } from '../../contexts/LocaleContext';
interface ConfigParamsPanelProps {
  section: string;
  params: ConfigParam[];
  canEdit: boolean;
}

/**
 * Configurable engagement parameters. Editing requires the
 * `engagement.manageConfig` capability, an explicit confirmation, and writes an
 * audit record with the previous and new value. REQUIRES BACKEND: authorization,
 * versioned storage and propagation to the app.
 */
export function ConfigParamsPanel({
  section,
  params,
  canEdit
}: ConfigParamsPanelProps) {
  const {
    tl
  } = useLocale();
  const [values, setValues] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<ConfigParam | null>(null);
  const [draft, setDraft] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [lastAudit, setLastAudit] = useState<AuditRecord | null>(null);
  const current = (param: ConfigParam) => values[param.id] ?? param.value;
  const display = (param: ConfigParam, raw: string) => {
    const option = param.options?.find(item => item.id === raw);
    if (option) return tl(option.label);
    return param.unit ? `${raw} ${tl(param.unit)}` : raw;
  };
  const invalid = editing !== null && (draft.trim() === '' || editing.kind === 'number' && (!/^\d+$/.test(draft.trim()) || Number(draft) < 0) || draft.trim() === current(editing));
  const open = (param: ConfigParam) => {
    setEditing(param);
    setDraft(current(param));
  };
  const apply = () => {
    if (!editing || !canEdit || invalid) return;
    const before = display(editing, current(editing));
    const after = display(editing, draft.trim());
    setValues(prev => ({
      ...prev,
      [editing.id]: draft.trim()
    }));
    const record = recordAudit({
      action: {
        'pt-BR': 'Alterou parâmetro de engajamento',
        'en-US': 'Changed engagement parameter'
      },
      entity: `engagement:${editing.id}`,
      context: {
        'pt-BR': `${section} · ${editing.label['pt-BR']}`,
        'en-US': `${section} · ${editing.label['en-US']}`
      },
      before,
      after,
      severity: 'sensitive'
    });
    setLastAudit(record);
    setConfirm(false);
    setEditing(null);
  };
  return <Panel title="Parâmetros configuráveis" hint={canEdit ? 'Valores vigentes da configuração de engajamento. Cada alteração exige confirmação e fica na auditoria.' : 'Valores vigentes. A edição exige a permissão “Gerenciar parâmetros de engajamento”.'} data-id="element-423" data-mp="glpxwjil7ybg">
      <ul data-id="element-424" data-mp="lgvhvcsfj2ww">
        {params.map(param => <li key={param.id} className="flex items-center justify-between gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-425" data-mp="10b0avpqy855v">
            <span className="min-w-0" data-id="element-426" data-mp="1eyikn2jdbpz5">
              <span className="block text-[12.5px] text-deep" data-id="element-427" data-mp="1ezjyferrqqkk">{tl(param.label)}</span>
              {param.hint && <span className="block text-[11px] text-muted" data-id="element-428" data-mp="1iz2f1yck4fhl">{tl(param.hint)}</span>}
            </span>
            <span className="flex shrink-0 items-center gap-3" data-id="element-429" data-mp="14zj9ndb4ri8z">
              <span className="bee-num text-right text-[12.5px] font-bold text-deep" data-id="element-430" data-mp="19z5qrxu2xin2">
                {display(param, current(param))}
              </span>
              {canEdit ? <Button variant="outline" onClick={() => open(param)} data-id="element-431" data-mp="10182598b5jtx">
                  Editar
                </Button> : <LockIcon size={13} className="text-muted" aria-label="Sem permissão para editar" data-id="element-432" data-mp="maw64z8lr0xi" />}
            </span>
          </li>)}
      </ul>

      {lastAudit && <div className="mt-3 rounded-card border border-sand-line bg-white px-3" role="status" data-id="element-433" data-mp="cbdrmybgo7cu">
          <p className="bee-overline pt-2 text-[8.5px] font-bold text-muted" data-id="element-434" data-mp="hb03b3bn5fjj">
            Registrado na auditoria
          </p>
          <ul data-id="element-435" data-mp="1kl4iqwk1acbx">
            <AuditEntry admin={lastAudit.admin} role={tl(roleLabels[lastAudit.role])} action={tl(lastAudit.action)} entity={lastAudit.entity} timestamp={lastAudit.timestamp} context={tl(lastAudit.context)} before={lastAudit.before} after={lastAudit.after} sensitive={lastAudit.severity === 'sensitive'} data-id="element-436" data-mp="1knqm0jzw65oc" />
          </ul>
        </div>}

      <Drawer open={editing !== null && !confirm} title={editing ? tl(editing.label) : ''} subtitle={section} onClose={() => setEditing(null)} footer={<>
            <Button variant="outline" onClick={() => setEditing(null)} data-id="element-438" data-mp="mzqb5vwvzv4i">
              Cancelar
            </Button>
            <Button variant="gold" disabled={invalid} onClick={() => setConfirm(true)} data-id="element-439" data-mp="2m8ndcd34irx0">
              Revisar alteração
            </Button>
          </>} data-id="element-437" data-mp="wui8qzqgyhza">
        {editing && <div className="space-y-3" data-id="element-440" data-mp="2otcy1y5nj0q0">
            <p className="text-[12.5px] text-muted" data-id="element-441" data-mp="2tu0n4eabl7hl">
              Valor atual:{' '}
              <span className="bee-num font-bold text-deep" data-id="element-442" data-mp="2ylipn6rzrg4o">{display(editing, current(editing))}</span>
            </p>
            <label className="block" data-id="element-443" data-mp="33klk5xaci3ye">
              <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-444" data-mp="33jk8bqli9dfm">Novo valor</span>
              {editing.options ? <select value={draft} onChange={event => setDraft(event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-445" data-mp="2avofwy2yy7on">
                  {editing.options.map(option => <option key={option.id} value={option.id} data-id="element-446" data-mp="3r9eig0efwnw6">
                      {tl(option.label)}
                    </option>)}
                </select> : <input value={draft} inputMode={editing.kind === 'number' ? 'numeric' : undefined} onChange={event => setDraft(event.target.value)} className="bee-num mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-447" data-mp="1w56qqvh3wgnn" />}
            </label>
            <p className="text-[11.5px] leading-snug text-muted" data-id="element-448" data-mp="1de5wuluufbay">
              A alteração vale para toda a base a partir da publicação e não muda regras fixas do
              produto.
            </p>
          </div>}
      </Drawer>

      <ConfirmationModal open={canEdit && confirm && editing !== null} destructive title="Confirmar alteração de parâmetro?" body={editing ? `${tl(editing.label)}: ${display(editing, current(editing))} → ${display(editing, draft.trim())}. A mudança vale para todos os usuários e fica registrada na auditoria com o valor anterior e o novo.` : ''} confirmLabel="Confirmar alteração" requireText="CONFIRMAR" onConfirm={apply} onClose={() => setConfirm(false)} data-id="element-449" data-mp="1hhlpl73skrid" />
    </Panel>;
}